import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, MaskedLines, SectionLabel } from "../components/Motion";
import { IMAGES, REGISTERED_COMPANY, PARTNER_CAPACITY } from "../lib/site";

export default function About() {
  return (
    <>
      <SEO />
      <div data-testid="about-page" className="bg-offwhite">
        <section className="pt-40 pb-20 sm:pb-28">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <SectionLabel index="ABOUT" title="PT Haqila Global Trading" />
            <MaskedLines
              lines={["About HAQILA"]}
              data-testid="about-primary-heading"
              className="mt-8 font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.98]"
              delay={0.15}
            />
          </div>
        </section>

        <section className="pb-20 sm:pb-28">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-14 items-start">
            <div className="lg:col-span-7 space-y-8">
              <Reveal>
                <p className="text-lg sm:text-xl text-charcoal/80 leading-relaxed font-medium">
                  HAQILA is an Indonesian sourcing and export partner connecting international B2B buyers with
                  selected Indonesian manufacturing partners and producers.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-base text-charcoal/65 leading-relaxed">
                  We are starting with coconut shell charcoal briquettes and gradually expanding into biomass, coffee,
                  coconut products, spices, cocoa and other Indonesian commodities.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="text-base text-charcoal/65 leading-relaxed">
                  Our approach is simple: understand the buyer's product specification, identify the right source,
                  review available quality documentation and manage export coordination.
                </p>
              </Reveal>
              <Reveal delay={0.2} className="border-l-2 border-forest pl-6 sm:pl-8 py-2">
                <p className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-snug">
                  HAQILA is not built around owning factories. It is built around knowing where to source.
                </p>
              </Reveal>
              <Reveal delay={0.25}>
                <p data-testid="about-capacity-stat" className="text-sm font-semibold text-charcoal/80 leading-relaxed">
                  {PARTNER_CAPACITY}
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="lg:col-span-5">
              <div className="relative overflow-hidden h-80 sm:h-96">
                <img
                  src={IMAGES.ownerWarehouse}
                  alt="HAQILA founder at the coconut shell charcoal warehouse in Indonesia"
                  loading="lazy"
                  data-testid="about-owner-photo"
                  className="absolute inset-0 w-full h-full object-cover img-spotlight"
                />
                <span className="absolute bottom-4 left-4 text-[10px] font-bold uppercase tracking-[0.25em] text-offwhite/80 bg-[#111111]/50 backdrop-blur-sm px-3 py-1.5">
                  At the source — Indonesia
                </span>
              </div>

              <div
                data-testid="about-registered-company"
                className="mt-6 border border-charcoal/15 bg-offwhite p-6 sm:p-7"
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-forest mb-4">
                  Registered Company
                </p>
                <dl className="space-y-3">
                  {REGISTERED_COMPANY.map((row) => (
                    <div key={row.label} className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-0.5">
                      <dt className="text-xs text-charcoal/50">{row.label}</dt>
                      <dd className="text-sm font-semibold text-charcoal text-right sm:text-right break-words">
                        {row.label === "Email" ? (
                          <a href={`mailto:${row.value}`} className="hover:text-forest transition-colors duration-300">
                            {row.value}
                          </a>
                        ) : (
                          row.value
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="bg-charcoal text-offwhite py-20 sm:py-28 tech-grid">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              { index: "01", title: "Sourcing First", copy: "Relationships with selected Indonesian manufacturers and producers at the source." },
              { index: "02", title: "Buyer Focused", copy: "Specifications, volumes and packaging are matched to your market requirements." },
              { index: "03", title: "Export Ready", copy: "Quotation, documentation and shipment coordination under one export partner." },
            ].map((item) => (
              <Reveal key={item.index} className="bg-charcoal p-10 sm:p-12 flex flex-col gap-6 h-full">
                <span className="font-display text-xs font-semibold tracking-[0.3em] text-clay">{item.index}</span>
                <h2 className="font-display text-2xl font-bold tracking-tight">{item.title}</h2>
                <p className="text-sm text-offwhite/55 leading-relaxed">{item.copy}</p>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="py-20 sm:py-28">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-10">
            <Reveal>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-xl leading-[1.02]">
                Work with a partner that understands Indonesian supply.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/request-a-quote"
                data-testid="about-quote-cta"
                className="group inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
              >
                Request a Quote
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </section>
      </div>
    </>
  );
}
